"use client";

import { useEffect, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";
import Link from "next/link";

import PortalNavBar from "./PortalNavBar";
import BankDepositButton from "./BridgeBankWithdrawalPanel";
import WithdrawButton from "./WithdrawButton";
import { backendFetch, getCurrentUser } from "../lib/backend";

export type PlatformRole =
  | "ADMINISTRATOR"
  | "SALESPERSON"
  | "IT_PERSON"
  | "BOOKKEEPER"
  | "PLATFORM_SALESPERSON";

type Mode = "dashboard" | "account" | "breakdown" | "revenue" | "earnings" | "distributions";

type Membership = {
  agreementId: number;
  agreementAddress: string;
  ownerWallet: string;
  agreementRole: string;
  active: boolean;
};

type Props = {
  role: PlatformRole;
  mode: Mode;
};

const REVENUE_DISTRIBUTOR_CONTRACT = process.env.NEXT_PUBLIC_PLATFORM_CONTRACT_ADDRESS ?? "";

const roleConfigs: Record<PlatformRole, { label: string; path: string }> = {
  ADMINISTRATOR: { label: "Administrator", path: "/admin" },
  SALESPERSON: { label: "Salesperson", path: "/salesperson" },
  IT_PERSON: { label: "IT Technician", path: "/it-person" },
  BOOKKEEPER: { label: "Bookkeeper", path: "/bookkeeper" },
  PLATFORM_SALESPERSON: { label: "Platform Salesperson", path: "/platform-salesperson" },
};

const modeTitles: Record<Mode, string> = {
  dashboard: "Workspace",
  account: "Profile",
  breakdown: "Accounts Breakdown",
  revenue: "Revenue History",
  earnings: "Earnings History",
  distributions: "Distribution History",
};

function amount(value: unknown) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : "0.00";
}

export default function PlatformStandardPage({ role, mode }: Props) {
  const roleConfig = roleConfigs[role];
  const { ready, authenticated, login, getAccessToken } = usePrivy();

  const defaultMemberships: Membership[] = [
    {
      agreementId: 1,
      agreementAddress: REVENUE_DISTRIBUTOR_CONTRACT,
      ownerWallet: "0x71C8F7960D8e6f1165A6836c01eC823725F7f474",
      agreementRole: roleConfig.label,
      active: true,
    },
  ];

  const defaultLive = {
    agreement: {
      revenueBalance: "12,750.00",
      participantCount: "4",
    },
    participant: {
      claimableBalance: "3,825.00",
    },
  };

  const defaultData = {
    history: [
      { id: 101, agreement_revenue: 5000, participant_distributions: 1500, created_at: "2026-09-11 14:00" },
      { id: 102, agreement_revenue: 7750, participant_distributions: 2325, created_at: "2026-09-10 11:30" },
    ],
    revenueHistory: [
      { id: 1, amount: 5000, created_at: "2026-09-11 14:00" },
      { id: 2, amount: 7750, created_at: "2026-09-10 11:30" },
    ],
    earnings: [
      { id: 1, amount: 1500, created_at: "2026-09-11 14:00" },
      { id: 2, amount: 2325, created_at: "2026-09-10 11:30" },
    ],
    distributions: [
      { id: 1, amount: 1500, created_at: "2026-09-11 14:00" },
      { id: 2, amount: 2325, created_at: "2026-09-10 11:30" },
    ],
  };

  const [adminAgreement, setAdminAgreement] = useState<any>(null);
  const [memberships, setMemberships] = useState<Membership[]>(defaultMemberships);
  const [selectedId, setSelectedId] = useState<number | null>(1);
  const [data, setData] = useState<any>(defaultData);
  const [live, setLive] = useState<any>(defaultLive);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ready || !authenticated) return;

    async function loadMemberships() {
      try {
        const token = await getAccessToken();
        if (!token) throw new Error("Authentication token unavailable.");

        const currentUser = await getCurrentUser(token);
        if (currentUser?.user?.platformRole && currentUser.user.platformRole !== role && currentUser.user.platformRole !== "ADMINISTRATOR") {
          throw new Error(`This page requires the ${roleConfig.label} role.`);
        }

        const [adminRes, meRes] = await Promise.all([
          backendFetch("/api/participant-access/admin-agreement", token),
          backendFetch("/api/participant-access/me", token),
        ]);

        if (adminRes.ok) {
          const payload = await adminRes.json();
          setAdminAgreement(payload.agreement ?? null);
        }

        if (!meRes.ok) {
          throw new Error(`Unable to verify agreement access (${meRes.status}).`);
        }

        const mePayload = await meRes.json();
        const items: Membership[] = mePayload.memberships ?? [];
        setMemberships(items);
        if (items.length > 0) {
          setSelectedId(items[0].agreementId);
        }
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : "Unable to load workspace.");
      } finally {
        setLoading(false);
      }
    }

    void loadMemberships();
  }, [authenticated, getAccessToken, ready, role, roleConfig.label]);

  useEffect(() => {
    if (!selectedId || !authenticated) return;

    async function loadReport() {
      try {
        const token = await getAccessToken();
        if (!token) throw new Error("Authentication token unavailable.");

        const requests = [
          backendFetch(`/api/participant-access/${selectedId}/live`, token),
        ];

        if (mode !== "account") {
          const endpoint = mode === "breakdown"
            ? `/api/participant-access/${selectedId}/breakdown`
            : `/api/participant-access/${selectedId}/revenue`;
          requests.push(backendFetch(endpoint, token));
        }

        const responses = await Promise.all(requests);
        if (responses[0].ok) {
          setLive(await responses[0].json());
        }

        if (responses[1] && responses[1].ok) {
          setData(await responses[1].json());
        }
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : "Unable to load report.");
      }
    }

    void loadReport();
  }, [authenticated, getAccessToken, mode, selectedId]);

  const selected = memberships.find((membership) => membership.agreementId === selectedId) ?? (authenticated ? memberships[0] : defaultMemberships[0]);
  const activeData = authenticated ? data : defaultData;
  const activeLive = authenticated ? live : defaultLive;

  const links = [
    { href: `${roleConfig.path}/dashboard`, label: "Dashboard", active: mode === "dashboard" },
    { href: `${roleConfig.path}/account`, label: "Account", active: mode === "account" },
    { href: `${roleConfig.path}/accounts-breakdown`, label: "Accounts Breakdown", active: mode === "breakdown" },
    { href: `${roleConfig.path}/revenue-history`, label: "Revenue History", active: mode === "revenue" },
    { href: `${roleConfig.path}/earnings-history`, label: "Earnings History", active: mode === "earnings" },
    { href: "/sales-history", label: "Sales History" },
    { href: "/admin/activity-log", label: "Activity Log" },
    { href: `${roleConfig.path}/distribution-history`, label: "Distribution History", active: mode === "distributions" },
  ];

  const title = mode === "dashboard" ? `${roleConfig.label} Workspace` : `${roleConfig.label} ${modeTitles[mode]}`;
  const records = mode === "revenue"
    ? activeData?.revenueHistory ?? []
    : mode === "earnings"
      ? activeData?.earnings ?? []
      : mode === "distributions"
        ? activeData?.distributions ?? []
        : mode === "breakdown"
          ? activeData?.history ?? []
          : [];

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-12 text-white md:px-8">
      <section className="mx-auto max-w-7xl">
        {!authenticated && (
          <div className="mb-6 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl border border-blue-500/30 bg-blue-500/10 p-4">
            <div className="text-xs text-blue-200">
              <span className="font-bold text-blue-300 uppercase tracking-wider mr-2">Simulated Workspace:</span>
              Previewing {roleConfig.label} dashboard with sample agreement allocations.
            </div>
            <button
              type="button"
              onClick={login}
              className="rounded-xl bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-500 shadow-md shadow-blue-600/30"
            >
              Sign In with Privy
            </button>
          </div>
        )}

        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-300">Platform participant portal</p>
            <h1 className="mt-3 text-4xl font-bold">{title}</h1>
            <p className="mt-2 max-w-2xl text-slate-300">Standard pages for the Admin-owned RevenueDistributor agreement.</p>
          </div>
          <Link href="/login" className="rounded-xl border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:border-slate-500">Role sign-in</Link>
        </div>

        <PortalNavBar links={links} portalTitle={`${roleConfig.label} Navigation`} />

        {error && <div className="mb-6 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{error}</div>}

        {loading ? (
          <p className="text-slate-400">Loading your RevenueDistributor agreement...</p>
        ) : memberships.length === 0 ? (
          <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-6 text-amber-200">
            {adminAgreement ? `Your ${roleConfig.label} account is not assigned to the Admin-owned RevenueDistributor agreement yet.` : "The Admin-owned RevenueDistributor agreement is not available."}
          </div>
        ) : (
          <>
            <div className="mb-8 flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-900 p-5 md:flex-row md:items-center md:justify-between">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 block mb-1">
                  Platform RevenueDistributor Agreement
                </span>
                <span className="text-sm font-medium text-white">
                  Core Platform Contract (AGPSIRARevenueDistributor-v1.0)
                </span>
              </div>
              <span className="break-all font-mono text-xs text-blue-300">{selected?.agreementAddress ?? adminAgreement?.agreementAddress ?? REVENUE_DISTRIBUTOR_CONTRACT}</span>
            </div>

            {live?.agreement && (
              <div className="mb-8 grid gap-4 sm:grid-cols-3">
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5">
                  <div className="text-sm text-emerald-200">Live agreement revenue</div>
                  <div className="mt-2 text-2xl font-bold text-emerald-300">${live.agreement.revenueBalance} USDC</div>
                </div>
                <div className="rounded-2xl border border-blue-500/30 bg-blue-500/10 p-5">
                  <div className="text-sm text-blue-200">Live participants</div>
                  <div className="mt-2 text-2xl font-bold text-blue-300">{live.agreement.participantCount}</div>
                </div>
                <div className="rounded-2xl border border-violet-500/30 bg-violet-500/10 p-5">
                  <div className="text-sm text-violet-200">Your claimable balance</div>
                  <div className="mt-2 text-2xl font-bold text-violet-300">{live.participant?.claimableBalance ?? "0"} USDC</div>
                </div>
              </div>
            )}

            {mode === "dashboard" || mode === "account" ? (
              <div className="grid gap-6 md:grid-cols-2">
                <article className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
                  <h2 className="text-xl font-semibold">Account Profile & Payout Settings</h2>
                  <dl className="mt-5 space-y-3 text-sm">
                    <div>
                      <dt className="text-slate-400">Role</dt>
                      <dd>{roleConfig.label}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-400">Agreement role</dt>
                      <dd>{selected?.agreementRole ?? "Platform participant"}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-400">Agreement source</dt>
                      <dd>Admin-owned RevenueDistributor</dd>
                    </div>
                  </dl>
                  <div className="mt-6 pt-4 border-t border-slate-800 space-y-3">
                    <WithdrawButton
                      contractType="REVENUE_DISTRIBUTOR"
                      label={`Withdraw ${roleConfig.label} Share`}
                    />
                    <BankDepositButton />
                  </div>
                </article>
                <article className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
                  <h2 className="text-xl font-semibold">Standard Pages</h2>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {links.slice(2).map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        className="rounded-xl border border-slate-700 p-4 text-sm text-blue-300 hover:border-blue-500"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </article>
              </div>
            ) : mode === "breakdown" ? (
              <div className="space-y-4">
                {records.length === 0 ? (
                  <p className="rounded-2xl border border-slate-800 bg-slate-900 p-6 text-slate-400">No breakdown records available.</p>
                ) : (
                  records.map((record: any, index: number) => (
                    <article key={record.id ?? index} className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
                      <div className="grid gap-4 sm:grid-cols-3">
                        <div>
                          <span className="text-sm text-slate-400">Agreement revenue</span>
                          <div className="mt-1 text-xl font-semibold">${amount(record.agreement_revenue)}</div>
                        </div>
                        <div>
                          <span className="text-sm text-slate-400">Participant distributions</span>
                          <div className="mt-1 text-xl font-semibold">${amount(record.participant_distributions)}</div>
                        </div>
                        <div>
                          <span className="text-sm text-slate-400">Recorded</span>
                          <div className="mt-1 text-sm text-slate-300">{record.created_at ?? "Latest snapshot"}</div>
                        </div>
                      </div>
                    </article>
                  ))
                )}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-800 text-slate-200">
                    <tr>
                      <th className="p-4 font-semibold">ID</th>
                      <th className="p-4 font-semibold">Amount</th>
                      <th className="p-4 font-semibold">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800 text-slate-300">
                    {records.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="p-4 text-slate-400">No records available.</td>
                      </tr>
                    ) : (
                      records.map((record: any, index: number) => (
                        <tr key={record.id ?? index}>
                          <td className="p-4">{record.id ?? index + 1}</td>
                          <td className="p-4 font-semibold text-emerald-400">${amount(record.amount ?? record.total_amount)}</td>
                          <td className="p-4">{record.created_at ?? "N/A"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
